/* ============================================================
   MRF Hide Gift Variants — hides $0 / "Gift (Free)" variants
   from product page UI so customers can't buy them directly.
   Variants stay live for programmatic cart-adds via the quiz.

   Also fixes "From $0.00" displayed on collection/search cards
   for products that have a Gift (Free) variant (product.price_min
   would otherwise pick the $0 variant). Replaces with the
   lowest PAID variant price.

   Install: add to layout/theme.liquid before </body>:
     {{ 'mrf-hide-gift-variants.js' | asset_url | script_tag }}

   How it works:
   A. On product detail page —
      1. Scans the page for product variant data (multiple theme conventions)
      2. Finds variants with price=0 OR SKU ending "-GIFT" OR title "Gift (Free)"
      3. Removes those variants from <select>, hides their radio buttons / labels,
         and rerouts the form to a paid variant if a gift one was preselected
   B. On collection / search / related products —
      1. Finds product cards linking to /products/{handle} where handle is in
         GIFT_PRODUCT_HANDLES below
      2. Fetches /products/{handle}.js (cached), computes min paid variant price
      3. Replaces "$0.00" / "From $0.00" text in the card's price area
   C. Re-runs after Shopify section reloads (quick-add modals, ajax product page)
   ============================================================ */

(function () {
  'use strict';

  const PATTERNS = {
    sku: /-GIFT$/i,
    title: /^\s*Gift\s*\(?\s*Free\s*\)?\s*$/i,
    optionValue: /^\s*Gift\s*\(?\s*Free\s*\)?\s*$/i
  };

  // Product handles that have a Gift (Free) variant. Cards linking to these
  // need their "From $0.00" price text fixed to the next-lowest variant.
  // Add more handles here if you introduce more gift products.
  const GIFT_PRODUCT_HANDLES = new Set([
    'mrf-leather-sleeve-hand-stitched-card-protector-for-edc-tools',
    'mrf-wallet-modular-leather-edc-cardholder-fits-all-mrf-tools'
  ]);

  // Specific gift VARIANT IDs — used to fix cart line item display
  // (Taiga's cart template renders the wrong price for these $0 variants)
  const GIFT_VARIANT_IDS = new Set([
    '58050694578563', // Leather Sleeve · Gift (Free)
    '58050695594371'  // MRF Wallet · Gift (Free)
  ]);

  // PREORDER-ONLY product handles. These products are NOT for direct sale on
  // their product pages — only via the quiz, where the user gets the preorder
  // experience (Kickstarter Summer 2026). On product pages we hide the
  // standard variant picker + Add to Cart and replace with "Pre-order via Quiz".
  const PREORDER_ONLY_PRODUCTS = new Set([
    'universal-5-0-credit-card-multitool',
    'bushcrafter-survival-card-multitool',
    'officeworker-40-in-1-blade-free-card-tool-flight-friendly-edc'
  ]);

  // Cache of fetched product data
  const productCache = new Map();

  function isGiftVariant(v) {
    if (!v) return false;
    if (Number(v.price) === 0 || Number(v.price) < 0.01) return true;
    if (v.sku && PATTERNS.sku.test(v.sku)) return true;
    if (v.title && PATTERNS.title.test(v.title)) return true;
    return false;
  }

  /**
   * Parse variant data from common JSON script locations across themes.
   * Returns an array of variant objects with at least { id, price, title, sku }.
   */
  function collectVariants() {
    const variants = [];
    const seen = new Set();
    const pushAll = (arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach(v => {
        if (!v || !v.id) return;
        const id = String(v.id);
        if (seen.has(id)) return;
        seen.add(id);
        variants.push({
          id,
          price: v.price,
          title: v.title,
          sku: v.sku,
          available: v.available
        });
      });
    };

    // Pattern 1: Dawn / Taiga style — script inside variant-radios or variant-selects
    document.querySelectorAll('variant-radios script[type="application/json"], variant-selects script[type="application/json"]').forEach(s => {
      try { pushAll(JSON.parse(s.textContent)); } catch (_) {}
    });

    // Pattern 2: <product-info> with embedded product json
    document.querySelectorAll('product-info script[type="application/json"]').forEach(s => {
      try {
        const data = JSON.parse(s.textContent);
        if (Array.isArray(data)) pushAll(data);
        else if (data && data.variants) pushAll(data.variants);
      } catch (_) {}
    });

    // Pattern 3: classic <script id="ProductJson-XXXX" type="application/json">
    document.querySelectorAll('script[id^="ProductJson"], script[data-product-json]').forEach(s => {
      try {
        const data = JSON.parse(s.textContent);
        if (data && Array.isArray(data.variants)) pushAll(data.variants);
      } catch (_) {}
    });

    // Pattern 4: window.product (older themes)
    if (window.product && Array.isArray(window.product.variants)) {
      pushAll(window.product.variants);
    }

    // Pattern 5: any <script type="application/ld+json"> with @type Product
    document.querySelectorAll('script[type="application/ld+json"]').forEach(s => {
      try {
        const data = JSON.parse(s.textContent);
        const offers = data && data.offers;
        if (Array.isArray(offers)) {
          offers.forEach(o => {
            if (o && o.sku) {
              variants.push({ id: 'ld:' + o.sku, sku: o.sku, price: o.price * 100, title: o.name });
            }
          });
        }
      } catch (_) {}
    });

    return variants;
  }

  /**
   * Hide variant by ID from any variant picker UI on the page.
   * Handles: <select>, radio buttons, custom button widgets, swatch lists.
   */
  function hideVariant(variantId) {
    const idStr = String(variantId);

    // <select name="id"> with <option value="VARIANT_ID">
    document.querySelectorAll('select[name="id"], select[name="options"], select.product-form__input').forEach(sel => {
      Array.from(sel.options).forEach(opt => {
        if (opt.value === idStr) {
          if (opt.selected) opt.selected = false;
          opt.remove();
        }
      });
      if (!sel.value && sel.options.length) {
        const first = Array.from(sel.options).find(o => !o.disabled);
        if (first) {
          sel.value = first.value;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Radio inputs <input type="radio" name="id" value="VARIANT_ID">
    document.querySelectorAll(`input[name="id"][value="${idStr}"], input[name="options"][value="${idStr}"]`).forEach(input => {
      if (input.checked) input.checked = false;
      hideEl(input);
      if (input.id) {
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (label) hideEl(label);
      }
    });

    // Custom data attributes commonly used by themes
    document.querySelectorAll(`[data-variant-id="${idStr}"], [data-id="${idStr}"], [data-option-value-id="${idStr}"]`).forEach(el => {
      hideEl(el);
    });
  }

  /**
   * Hide elements that contain "Gift (Free)" text — covers swatch buttons,
   * fieldset legends, dropdown labels that aren't ID-linked.
   */
  function hideByText() {
    const candidates = document.querySelectorAll(
      'fieldset.product-form__input label, .product-form__input--option-value, .variant-option, .swatch__input + label, .swatch-button, [class*="variant-input"] label'
    );
    candidates.forEach(el => {
      const text = (el.textContent || '').trim();
      if (PATTERNS.optionValue.test(text)) {
        hideEl(el);
        const input = el.querySelector('input') || (el.htmlFor ? document.getElementById(el.htmlFor) : null);
        if (input) {
          if (input.checked) input.checked = false;
          hideEl(input);
        }
      }
    });
  }

  function hideEl(el) {
    if (!el || el._mrfHidden) return;
    el._mrfHidden = true;
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
    // Also try to hide a parent wrapper if it would leave an empty fieldset row
    const wrapper = el.closest('li, .product-form__input--option-value, .variant-option');
    if (wrapper && wrapper.children.length === 1) hideEl(wrapper);
  }

  /**
   * Fetch /products/{handle}.js once, cache result.
   * Returns the product JSON or null.
   */
  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const promise = fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    productCache.set(handle, promise);
    return promise;
  }

  /**
   * Format a price (in cents) as a string matching how the storefront displays it.
   * E.g. 800 → "8.00", 2400 → "24.00", 1950 → "19.50".
   */
  function formatPriceCents(cents) {
    const dollars = Number(cents) / 100;
    return dollars.toFixed(2);
  }

  /**
   * Within a card element, find price-displaying nodes and replace any
   * "$0.00", "From $0.00" patterns with the given paid-price string.
   */
  function replaceZeroPriceIn(card, newPriceStr) {
    // Look for nodes that contain "$0.00" or "0.00 USD" etc.
    const walker = document.createTreeWalker(card, NodeFilter.SHOW_TEXT, {
      acceptNode(n) {
        if (!n.nodeValue) return NodeFilter.FILTER_REJECT;
        return /(\$|\s)0\.00(\s|$|[A-Z])/.test(n.nodeValue)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    const nodes = [];
    let cur;
    while ((cur = walker.nextNode())) nodes.push(cur);

    nodes.forEach(node => {
      // Replace "$0.00" specifically, leave surrounding "From " or " USD" intact
      node.nodeValue = node.nodeValue.replace(/\$\s*0\.00/g, `$${newPriceStr}`);
      // Also handle " 0.00 USD" pattern without dollar sign
      node.nodeValue = node.nodeValue.replace(/(^|\s)0\.00(\s|$)/g, `$1${newPriceStr}$2`);
    });
  }

  /**
   * Scan the page for product cards linking to GIFT_PRODUCT_HANDLES,
   * fetch their JSON, compute min paid variant price, replace $0 in card.
   */
  async function fixZeroPriceCards() {
    const links = document.querySelectorAll('a[href*="/products/"]');
    const targets = new Map(); // handle → array of card elements

    links.forEach(link => {
      const m = link.getAttribute('href') || '';
      const handleMatch = m.match(/\/products\/([^/?#]+)/);
      if (!handleMatch) return;
      const handle = handleMatch[1];
      if (!GIFT_PRODUCT_HANDLES.has(handle)) return;

      // Climb up to a card-like wrapper
      const card = link.closest(
        '.card-wrapper, .card, .product-card, .grid__item, [class*="product-item"], [class*="collection-item"], li, article'
      ) || link.parentElement;
      if (!card || card._mrfPriceFixed) return;
      card._mrfPriceFixed = true; // mark so we don't redo

      if (!targets.has(handle)) targets.set(handle, []);
      targets.get(handle).push(card);
    });

    if (!targets.size) return;

    for (const [handle, cards] of targets) {
      const product = await fetchProduct(handle);
      if (!product || !Array.isArray(product.variants)) continue;
      const paid = product.variants.filter(v => Number(v.price) > 0);
      if (!paid.length) continue;
      const minCents = Math.min(...paid.map(v => Number(v.price)));
      const priceStr = formatPriceCents(minCents);
      cards.forEach(card => replaceZeroPriceIn(card, priceStr));
    }
  }

  /**
   * Fix the cart-page line-item display where Taiga's theme template
   * renders the wrong price ($24, the product's max variant price) for
   * our $0 Gift (Free) variants. Replaces visible "$24.00" with "FREE".
   * Runs on cart page + cart drawer + mini-cart popups.
   */
  function fixGiftLineItemsInCart() {
    GIFT_VARIANT_IDS.forEach(idStr => {
      // Find any element referencing the variant ID
      const refs = document.querySelectorAll(
        `[data-variant-id="${idStr}"], [data-id="${idStr}"], ` +
        `[data-line-item-variant-id="${idStr}"], [data-variant="${idStr}"], ` +
        `[href*="variant=${idStr}"]`
      );
      refs.forEach(ref => {
        const row = ref.closest(
          'tr, li, .cart-item, .cart__row, .cart-line-item, ' +
          '[class*="cart-line"], [class*="CartLineItem"], [class*="CartItem"], ' +
          '[class*="line-item"], article'
        );
        if (!row || row._mrfGiftPriceFixed) return;
        row._mrfGiftPriceFixed = true;

        // Replace price text inside row
        const priceEls = row.querySelectorAll(
          '.price, .cart-item__price, .cart-item__price-wrapper, ' +
          '.cart__price, .CartItem__Price, [class*="price"]:not([class*="total"]):not([class*="subtotal"]), ' +
          '.money, [data-cart-item-price], [data-cart-item-final-price]'
        );
        priceEls.forEach(el => {
          if (el._mrfReplaced) return;
          const html = el.innerHTML;
          const newHtml = html.replace(
            /\$\s*[\d,]+(?:\.\d{2})?\s*(?:USD|EUR|GBP|UAH|CAD|AUD)?/gi,
            '<span style="color: #4FAE8C; font-weight: 700;">FREE</span>'
          );
          if (newHtml !== html) {
            el._mrfReplaced = true;
            el.innerHTML = newHtml;
          }
        });
      });
    });

    // Fallback: match by variant title text "Gift (Free)" + nearby price
    const candidates = document.querySelectorAll(
      '.cart-item, .cart__row, [class*="cart-line"], [class*="CartItem"], [class*="line-item"]'
    );
    candidates.forEach(row => {
      if (row._mrfGiftPriceFixed) return;
      const txt = (row.textContent || '').toLowerCase();
      if (!/gift\s*\(?\s*free\s*\)?/.test(txt)) return;
      row._mrfGiftPriceFixed = true;
      const priceEls = row.querySelectorAll(
        '.price, .cart-item__price, .money, [class*="price"]:not([class*="total"]):not([class*="subtotal"])'
      );
      priceEls.forEach(el => {
        if (el._mrfReplaced) return;
        const html = el.innerHTML;
        const newHtml = html.replace(
          /\$\s*[\d,]+(?:\.\d{2})?\s*(?:USD|EUR|GBP|UAH|CAD|AUD)?/gi,
          '<span style="color: #4FAE8C; font-weight: 700;">FREE</span>'
        );
        if (newHtml !== html) {
          el._mrfReplaced = true;
          el.innerHTML = newHtml;
        }
      });
    });
  }

  /**
   * On a PREORDER_ONLY product page, replace the variant picker + Add to Cart
   * with a compact "Pre-order via Quiz" banner. Mobile-first: small footprint
   * so it doesn't dominate the viewport. Also detects any sticky/fixed wrapper
   * around the cart form (Taiga's mobile sticky-cart pattern) and hides it
   * entirely so the banner sits truly inline with product content.
   */
  function injectPreorderStyles() {
    if (document.getElementById('mrf-preorder-styles')) return;
    const s = document.createElement('style');
    s.id = 'mrf-preorder-styles';
    s.textContent = `
      .mrf-preorder-block {
        position: static !important;
        background: linear-gradient(135deg, #FFF6E5 0%, #FFE4BA 100%);
        border: 1.5px solid #E5B978;
        border-radius: 12px;
        padding: 12px 14px;
        margin: 12px 0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
        color: #1A1816;
        line-height: 1.35;
      }
      .mrf-preorder-block__hd {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 10px;
        color: #2A2520;
      }
      .mrf-preorder-block__hd strong { font-weight: 800; }
      .mrf-preorder-block__cta {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #1A1816;
        color: #FFE4BA !important;
        text-decoration: none !important;
        padding: 10px 18px;
        border-radius: 999px;
        font-weight: 700;
        font-size: 13px;
        box-shadow: 0 3px 10px rgba(0,0,0,0.15);
        cursor: pointer;
        border: none;
        white-space: nowrap;
      }
      .mrf-preorder-block__cta:hover { background: #2A2520; }
      @media (min-width: 768px) {
        .mrf-preorder-block { padding: 18px 22px; border-radius: 14px; margin: 16px 0; }
        .mrf-preorder-block__hd { font-size: 15px; margin-bottom: 12px; }
        .mrf-preorder-block__cta { padding: 12px 24px; font-size: 14px; }
      }
    `;
    document.head.appendChild(s);
  }

  function blockPreorderOnlyProductPage() {
    const path = window.location.pathname;
    const match = path.match(/^\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = match[1];
    if (!PREORDER_ONLY_PRODUCTS.has(handle)) return;

    injectPreorderStyles();

    // Hide cart form UI and inject the compact preorder banner.
    // Note: we deliberately do NOT touch sticky/fixed ancestors — Taiga wraps
    // the entire product info column (title/price/description/form) in
    // position:sticky, so hiding that ancestor would blank the page.
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      if (form._mrfPreorderBlocked) return;
      form._mrfPreorderBlocked = true;

      Array.from(form.children).forEach(c => { c.style.display = 'none'; });
      form.querySelectorAll('button[type="submit"], button[name="add"], [class*="add-to-cart"], [class*="add_to_cart"]')
        .forEach(btn => { btn.style.display = 'none'; });

      const wrap = document.createElement('div');
      wrap.className = 'mrf-preorder-block';
      wrap.innerHTML = `
        <div class="mrf-preorder-block__hd">
          🚀 <strong>Pre-order Only</strong> · Kickstarter Summer 2026 · Steel
        </div>
        <a href="#mrf-quiz" data-quiz-trigger class="mrf-preorder-block__cta">
          🎯 Take the quiz to pre-order
        </a>
      `;
      form.parentNode.insertBefore(wrap, form);
    });
  }

  let runScheduled = false;
  function run() {
    if (runScheduled) return;
    runScheduled = true;
    requestAnimationFrame(() => {
      runScheduled = false;
      try {
        const variants = collectVariants();
        const giftIds = variants.filter(isGiftVariant).map(v => v.id).filter(id => !id.startsWith('ld:'));
        if (giftIds.length) {
          giftIds.forEach(hideVariant);
        }
        // Always also sweep for "Gift (Free)" text occurrences (catches LD-only themes)
        hideByText();
        // Fix product cards on collection/search/related pages
        fixZeroPriceCards();
        // Fix the cart-line-item display where Taiga shows $24 for a $0 variant
        fixGiftLineItemsInCart();
        // Block direct purchase on preorder-only product pages (U5, Bush, Office)
        blockPreorderOnlyProductPage();
      } catch (e) {
        console.warn('[MRF Hide Gift] error', e);
      }
    });
  }

  function init() {
    run();

    // Re-run after Shopify Section Rendering API reloads (theme editor, quick-add, drawer cart)
    document.addEventListener('shopify:section:load', run);
    document.addEventListener('shopify:section:reorder', run);
    document.addEventListener('shopify:block:select', run);

    // Re-run after AJAX product fetches some themes use
    const origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function (...args) {
        const result = origFetch.apply(this, args);
        try {
          const url = (typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url)) || '';
          if (url.includes('/products/') && url.includes('.js')) {
            result.then(() => setTimeout(run, 50));
          }
        } catch (_) {}
        return result;
      };
    }

    // MutationObserver fallback — re-run when variant pickers are added/replaced
    if (window.MutationObserver) {
      const obs = new MutationObserver(muts => {
        for (const m of muts) {
          for (const node of m.addedNodes) {
            if (node.nodeType === 1 && (
              node.matches?.('variant-radios, variant-selects, product-info, .product-form, .product__info') ||
              node.querySelector?.('variant-radios, variant-selects, select[name="id"], input[name="id"]')
            )) {
              run();
              return;
            }
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
